import { SURFACE_HEADER, type UsagePingRequest, type UsageSurface } from '@filmsloth/shared';
import { isStandalone } from './installPrompt.js';

/** Same origin unless a deploy points the client somewhere else. */
const BASE = import.meta.env.VITE_API_BASE ?? '';

/**
 * Telling the server a screen was opened.
 *
 * This is the whole client half of FilmSloth's statistics, and it is
 * deliberately the *smaller* half: everything that matters — rounds started,
 * films settled on, accounts made — is counted by the server when the action
 * succeeds, so a blocked request here costs a page-view tally and nothing
 * else.
 *
 * Three rules it keeps:
 *
 * - **Nothing is stored on the device.** No identifier, no cookie, no local
 *   storage key. That is what makes the numbers aggregate: there is nothing
 *   to string two visits together with, here or on the server.
 * - **The cookies that do exist are not sent.** `credentials: 'omit'`, so the
 *   login cookie is not even on the wire for a request that has no business
 *   knowing who is signed in.
 * - **A failure is not an error.** Offline, blocked, refused: the app never
 *   notices and the person never sees anything.
 */

/** Which shell this page is running in: installed app, or browser tab. */
export function currentSurface(): UsageSurface {
  try {
    return isStandalone() ? 'app' : 'web';
  } catch {
    // A browser that answers neither `display-mode` nor `navigator.standalone`
    // is better counted as "did not say" than guessed at.
    return 'unknown';
  }
}

/**
 * Screens already reported for this page load.
 *
 * Per load rather than per screen change, so `view:home` counts *loads that
 * reached the home screen* — a number that means something — instead of how
 * often somebody happened to tap Back. It also makes the tally immune to a
 * re-render or React's development double-mount, which would otherwise show
 * up as traffic that never happened.
 */
const reported = new Set<string>();
let visitReported = false;

/** Reports a screen, at most once per page load. */
export function reportView(view: UsagePingRequest['view']): void {
  if (reported.has(view)) return;
  reported.add(view);

  // The first screen of a load is also the visit. Anything after it is
  // navigation inside the same visit, which is a different question.
  const visit = !visitReported;
  visitReported = true;

  void send({ view, visit });
}

async function send(body: UsagePingRequest): Promise<void> {
  try {
    await fetch(`${BASE}/api/stats/views`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', [SURFACE_HEADER]: currentSurface() },
      body: JSON.stringify(body),
      credentials: 'omit',
      // Survives the page going away mid-request, which a ping fired on the
      // way into a screen somebody immediately leaves otherwise would not.
      keepalive: true,
    });
  } catch {
    // Counting is the least important thing this app does. It never gets to
    // surface an error.
  }
}

/** Forgets what this load has reported. For tests only. */
export function resetUsageReporting(): void {
  reported.clear();
  visitReported = false;
}
